import {
    DateRangePicker,
    type DateRangePickerValue,
} from '@apps/web/components/ui/date_range_picker';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { ANALYTICS_TIMEZONE } from '@apps/web/features/admin_agent_notes/model/constants';
import {
    AGENT_NOTE_RANGE_PRESETS,
    type AgentNoteRangePreset,
    RANGE_PRESET_LABELS,
    type RangeSelection,
    resolveRangePreset,
} from '@apps/web/features/admin_agent_notes/model/range_presets';
import { getDateStringInTimezone } from '@apps/web/lib/date_utils';

const ALL_TIME_VALUE = '__all';
const CUSTOM_VALUE = '__custom';

export { type RangeSelection } from '@apps/web/features/admin_agent_notes/model/range_presets';

/**
 * Date-range control with relative presets ("Last 14 days") plus a custom
 * fixed range. Relative choices stay relative when the URL is pinned; the
 * fixed picker appears only when "Custom range" is selected.
 */
export function RangeControl({
    preset,
    fixed,
    defaultPreset,
    allowAllTime = false,
    onChange,
}: {
    readonly preset: AgentNoteRangePreset | undefined;
    readonly fixed: { readonly from?: string; readonly to?: string } | null;
    readonly defaultPreset?: AgentNoteRangePreset;
    readonly allowAllTime?: boolean;
    readonly onChange: (selection: RangeSelection) => void;
}) {
    const today = getDateStringInTimezone(new Date(), ANALYTICS_TIMEZONE);
    const isFixed = fixed !== null;
    const selectValue = isFixed
        ? CUSTOM_VALUE
        : (preset ?? defaultPreset ?? ALL_TIME_VALUE);
    const fixedValue: DateRangePickerValue = {
        from:
            fixed?.from ??
            resolveRangePreset(preset ?? defaultPreset ?? '30d', today).from,
        to: fixed?.to ?? today,
    };

    return (
        <div className="flex items-center gap-2">
            <Select
                value={selectValue}
                onValueChange={(value) => {
                    if (value === ALL_TIME_VALUE) {
                        onChange({ kind: 'all' });
                        return;
                    }
                    if (value === CUSTOM_VALUE) {
                        onChange({
                            kind: 'fixed',
                            from: fixedValue.from,
                            to: fixedValue.to,
                        });
                        return;
                    }
                    const nextPreset = AGENT_NOTE_RANGE_PRESETS.find(
                        (candidate) => candidate === value,
                    );
                    if (nextPreset) {
                        onChange({ kind: 'preset', preset: nextPreset });
                    }
                }}
            >
                <SelectTrigger size="sm" className="w-[150px]">
                    <SelectValue placeholder="Date range" />
                </SelectTrigger>
                <SelectContent>
                    {allowAllTime && (
                        <SelectItem value={ALL_TIME_VALUE}>All time</SelectItem>
                    )}
                    {AGENT_NOTE_RANGE_PRESETS.map((candidate) => (
                        <SelectItem key={candidate} value={candidate}>
                            {RANGE_PRESET_LABELS[candidate]}
                        </SelectItem>
                    ))}
                    <SelectItem value={CUSTOM_VALUE}>Custom range…</SelectItem>
                </SelectContent>
            </Select>
            {isFixed && (
                <DateRangePicker
                    className="w-auto"
                    value={fixedValue}
                    timezone={ANALYTICS_TIMEZONE}
                    onChange={(next) => {
                        onChange({
                            kind: 'fixed',
                            from: next.from,
                            to: next.to,
                        });
                    }}
                />
            )}
        </div>
    );
}
